import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { canUserDelete, canUserEdit } from '@/utils/permissions';
import { Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import {
  ShoppingCart, Plus, Search, Edit2, Trash2, Save, X, RefreshCw,
  FileText, Download, Eye, ChevronDown, ChevronUp, Package, Building2,
  Calendar, DollarSign, Truck, CheckCircle, AlertCircle, Loader2, Warehouse,
  History, Mail
} from 'lucide-react';
import EditHistoryModal from '../../components/EditHistoryModal';
import CommodityNameSuggestion from '../../components/CommodityNameSuggestion';
import HsnCommodityPicker from '../../components/HsnCommodityPicker';
import EmailModal from '../../components/EmailModal';
import EmailStatusBadge, { isUnemailed } from '../../components/EmailStatusBadge';
import AdvancePaymentSplitConfig from '../../components/AdvancePaymentSplitConfig';

const STATUS_OPTIONS = ['Draft', 'Sent', 'Confirmed', 'Partially Received', 'Completed', 'Cancelled'];

const CURRENCY_OPTIONS = [
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc' },
  { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal' },
];

// Custom Async Searchable Dropdown for Vendors
function VendorDropdown({ value, displayValue, onSelect, placeholder = "Search vendor...", disabled = false }) {
  const { API } = useContext(AppContext);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const handleSearch = useCallback(async (term) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    
    searchTimeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/master-data/stores/vendors`);
        const vendors = response.data.data || [];
        const filtered = vendors.filter(v => 
          v.vendorName?.toLowerCase().includes(term.toLowerCase()) ||
          v.vendorCode?.toLowerCase().includes(term.toLowerCase())
        ).slice(0, 20);
        setOptions(filtered);
      } catch (error) {
        console.error('Error loading vendors:', error);
        setOptions([]);
      }
      setLoading(false);
    }, 300);
  }, [API]);

  useEffect(() => {
    if (isOpen) handleSearch(searchTerm);
  }, [isOpen, searchTerm, handleSearch]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-lg text-sm text-left flex items-center justify-between ${
          disabled ? 'bg-gray-100 border-gray-200' : 'border-gray-300 hover:border-amber-400'
        }`}
        data-testid="vendor-dropdown"
      >
        <span className={displayValue ? 'text-gray-900' : 'text-gray-400'}>
          {displayValue || placeholder}
        </span>
        <ChevronDown size={16} className={`text-gray-400 transition ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-72 overflow-hidden">
          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
              <Search size={16} className="text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type to search..."
                className="flex-1 bg-transparent border-none outline-none text-sm"
                autoFocus
              />
              {loading && <Loader2 size={16} className="animate-spin text-gray-400" />}
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto">
            {options.length === 0 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">
                {loading ? 'Loading...' : 'No vendors found'}
              </div>
            ) : (
              options.map((vendor) => (
                <button
                  key={vendor.id}
                  type="button"
                  onClick={() => { onSelect(vendor); setIsOpen(false); setSearchTerm(''); }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-amber-50 transition flex items-center gap-2"
                >
                  <Building2 size={14} className="text-gray-400" />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{vendor.vendorName}</div>
                    <div className="text-xs text-gray-500">
                      {vendor.vendorCode} | {vendor.city || 'N/A'}
                      {vendor.gstin && <span className="ml-2 text-amber-600">GST: {vendor.gstin}</span>}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Custom Async Searchable Dropdown for Commodities
function CommodityDropdown({ value, displayValue, onSelect, placeholder = "Search commodity...", disabled = false }) {
  const { API } = useContext(AppContext);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const handleSearch = useCallback(async (term) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    
    if (term.length < 2) {
      setOptions([]);
      setLoading(false);
      return;
    }
    
    searchTimeoutRef.current = setTimeout(async () => {
      setLoading(true);
      
      // Fire BOTH requests in parallel
      const [commodityResult, sacResult] = await Promise.allSettled([
        axios.get(`${API}/master-data/stores/commodities/search`, { params: { q: term, limit: 20 } }),
        axios.get(`${API}/master-data/stores/service_sac/search`, { params: { q: term, limit: 20 } })
      ]);
      
      let results = [];
      
      // Process commodity results
      if (commodityResult.status === 'fulfilled') {
        results = commodityResult.value.data?.data || [];
      }
      
      // Process SAC results 
      if (sacResult.status === 'fulfilled') {
        const sacRaw = sacResult.value.data?.data || sacResult.value.data || [];
        const sacFormatted = sacRaw.map(sac => {
          // Handle both formatted (from dedicated endpoint) and raw SAC records
          if (sac.commodityCode) return sac; // Already formatted
          return {
            id: `sac_${sac.sacCode || ''}`,
            commodityCode: sac.sacCode || '',
            commodityName: sac.sacDescription || '',
            hsnCode: sac.sacCode || '',
            gstRate: sac.gstRate || 18,
            cgst: sac.cgst || 9,
            sgst: sac.sgst || 9,
            igst: sac.igst || 18,
            category: 'Service',
            itemType: 'service',
            status: 'Active'
          };
        });
        const existingCodes = new Set(results.map(r => r.commodityCode));
        results = [...results, ...sacFormatted.filter(s => !existingCodes.has(s.commodityCode))];
      }
      
      setOptions(results);
      setLoading(false);
    }, 300);
  }, [API]);

  useEffect(() => {
    if (isOpen && searchTerm.length >= 2) handleSearch(searchTerm);
  }, [isOpen, searchTerm, handleSearch]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-lg text-sm text-left flex items-center justify-between ${
          disabled ? 'bg-gray-100 border-gray-200' : 'border-gray-300 hover:border-amber-400'
        }`}
      >
        <span className={displayValue ? 'text-gray-900 truncate' : 'text-gray-400'}>
          {displayValue || placeholder}
        </span>
        <ChevronDown size={16} className={`text-gray-400 transition flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-72 overflow-hidden">
          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
              <Search size={16} className="text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type at least 2 characters..."
                className="flex-1 bg-transparent border-none outline-none text-sm"
                autoFocus
              />
              {loading && <Loader2 size={16} className="animate-spin text-gray-400" />}
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto">
            {searchTerm.length < 2 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">
                Type at least 2 characters to search
              </div>
            ) : options.length === 0 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">
                {loading ? 'Loading...' : 'No commodities or SAC codes found'}
              </div>
            ) : (
              options.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => { onSelect(item); setIsOpen(false); setSearchTerm(''); }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-amber-50 transition"
                >
                  <div className="font-medium text-gray-900 truncate">
                    {item.itemType === 'service' && <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded mr-1">SAC</span>}
                    {item.commodityName}
                  </div>
                  <div className="text-xs text-gray-500">
                    {item.itemType === 'service' ? 'SAC' : 'HSN'}: {item.hsnCode} | GST: {item.gstRate}%
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// SAC Code Search Dropdown for Service POs
function SACSearchDropdown({ value, displayValue, onSelect, placeholder = "Search SAC code or service...", disabled = false }) {
  const { API } = useContext(AppContext);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const handleSearch = useCallback(async (term) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (term.length < 2) { setOptions([]); setLoading(false); return; }
    searchTimeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/stores/service-sac/search`, { params: { q: term, limit: 30 } });
        setOptions(response.data.data || []);
      } catch (error) {
        console.error('Error loading SAC codes:', error);
        setOptions([]);
      }
      setLoading(false);
    }, 300);
  }, [API]);

  useEffect(() => {
    if (isOpen && searchTerm.length >= 2) handleSearch(searchTerm);
  }, [isOpen, searchTerm, handleSearch]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-lg text-sm text-left flex items-center justify-between ${
          disabled ? 'bg-gray-100 border-gray-200' : 'border-gray-300 hover:border-indigo-400'
        }`}
      >
        <span className={displayValue ? 'text-gray-900 truncate' : 'text-gray-400'}>
          {displayValue || placeholder}
        </span>
        <ChevronDown size={16} className={`text-gray-400 transition flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-72 overflow-hidden">
          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
              <Search size={16} className="text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type SAC code or service name..."
                className="flex-1 bg-transparent border-none outline-none text-sm"
                autoFocus
              />
              {loading && <Loader2 size={16} className="animate-spin text-gray-400" />}
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto">
            {searchTerm.length < 2 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">Type at least 2 characters to search</div>
            ) : options.length === 0 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">{loading ? 'Loading...' : 'No SAC codes found'}</div>
            ) : (
              options.filter(item => item.codeLength === 6).map((item) => (
                <button
                  key={item.sacCode}
                  type="button"
                  onClick={() => { onSelect(item); setIsOpen(false); setSearchTerm(''); }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-indigo-50 transition"
                >
                  <div className="font-medium text-gray-900 truncate">{item.sacDescription}</div>
                  <div className="text-xs text-gray-500">SAC: {item.sacCode} | GST: {item.gstRate}%</div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Custom dropdown for adding Terms & Conditions (native <select> cannot wrap long option text)
function TermsConditionsDropdown({ options, onAdd }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const term = searchTerm.toLowerCase();
  const filtered = options.filter(tc =>
    (tc.termDescription || tc.termTitle || '').toLowerCase().includes(term)
  );

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-left flex items-center justify-between hover:border-amber-400 focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
        data-testid="add-terms-dropdown-button"
      >
        <span className="text-gray-400">+ Add terms &amp; conditions...</span>
        <ChevronDown size={16} className={`text-gray-400 shrink-0 transition ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-72 overflow-hidden" data-testid="add-terms-dropdown-panel">
          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
              <Search size={16} className="text-gray-400 shrink-0" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type to search..."
                className="flex-1 bg-transparent border-none outline-none text-sm"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">No terms found</div>
            ) : (
              filtered.map((tc) => (
                <button
                  key={tc.id}
                  type="button"
                  onClick={() => { onAdd(tc.id); setSearchTerm(''); }}
                  className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-amber-50 transition whitespace-normal break-words"
                  data-testid={`add-term-option-${tc.id}`}
                >
                  {tc.termDescription || tc.termTitle}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}


export default function StoresPurchaseOrder() {
  const { API, user } = useContext(AppContext);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPO, setEditingPO] = useState(null);
  const [expandedPO, setExpandedPO] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [message, setMessage] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [historyPO, setHistoryPO] = useState(null);
  const [emailPO, setEmailPO] = useState(null);
  const [showOnlyUnemailed, setShowOnlyUnemailed] = useState(false);
  const [poTab, setPoTab] = useState('material'); // 'material' or 'service'

  /**
   * Open the Email PO modal with the LATEST vendor email from master data.
   * The PO record's `vendorEmail` is a snapshot taken at PO creation time;
   * if the user updated the vendor's email in Master Data later, the snapshot
   * is stale. We always re-fetch by vendorId (preferred) or vendorName here
   * so the TO field is correct without requiring the PO to be re-saved.
   */
  const openEmailModal = async (po) => {
    if (!po) return;
    try {
      const r = await axios.get(`${API}/master-data/stores/vendors`);
      const vendors = r?.data?.data || [];
      const match = vendors.find(v =>
        (po.vendorId && v.id === po.vendorId) ||
        (po.vendorCode && v.vendorCode === po.vendorCode) ||
        (po.vendorName && (v.vendorName || '').trim().toLowerCase() === (po.vendorName || '').trim().toLowerCase())
      );
      const freshEmail = match?.email || '';
      // Stash both: the fresh email (defaultTo) AND the original snapshot
      // so the modal can warn the user if they differ.
      setEmailPO({
        ...po,
        vendorEmail: freshEmail || po.vendorEmail || '',
        snapshotEmail: po.vendorEmail || '',
      });
    } catch (e) {
      // Network or auth blip → fall back to the snapshot already on the PO
      setEmailPO({ ...po, snapshotEmail: po.vendorEmail || '' });
    }
  };
  
  // Check if user can delete (Admin or 'full' access)
  const canDelete = canUserDelete(user, '/stores/purchase-order');
  const canEdit = canUserEdit(user, '/stores/purchase-order');

  // PO Value Filter State
  const [valueFilterType, setValueFilterType] = useState('all'); // all, month, year, custom
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  // Form state
  const [formData, setFormData] = useState({
    vendorName: '',
    vendorCode: '',
    vendorGst: '',
    vendorContact: '',
    vendorContactPerson: '',
    vendorEmail: '',
    vendorAddress: '',
    vendorCity: '',
    vendorState: '',
    vendorCountry: '',
    poDate: new Date().toISOString().split('T')[0],
    deliveryDate: '',
    deliveryEndDate: '',
    paymentTerms: '',
    currency: 'INR', // Currency field - default to INR for Stores
    termsConditions: [], // Changed to array for multiple T&C selection
    remarks: '',
    items: [{ commodityId: '', commodityName: '', commodityCode: '', hsnCode: '', gstRate: '', quantity: '', uom: 'Nos', rate: '', amount: 0 }],
    miscCharges: [],
    advancePaymentConfig: { enabled: false, splits: [] },
  });
  
  // Get currency symbol
  const getCurrencySymbol = (code) => {
    const currency = CURRENCY_OPTIONS.find(c => c.code === code);
    return currency ? currency.symbol : '₹';
  };

  // Master data for dropdowns
  const [paymentTermsList, setPaymentTermsList] = useState([]);
  const [termsConditionsList, setTermsConditionsList] = useState([]);
  const [uomList, setUomList] = useState([]); // UOM from master data

  useEffect(() => {
    fetchPurchaseOrders();
    fetchMasterData();
  }, [poTab]);

  const fetchPurchaseOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stores/purchase-orders-v2`, { params: { poType: poTab } });
      setPurchaseOrders(response.data.data || []);
    } catch (error) {
      console.error('Error fetching POs:', error);
    }
    setLoading(false);
  };

  const fetchMasterData = async () => {
    try {
      const paymentRes = await axios.get(`${API}/master-data/stores/payment_terms`);
      setPaymentTermsList(paymentRes.data.data || []);

      const termsRes = await axios.get(`${API}/master-data/stores/terms_conditions`);
      setTermsConditionsList(termsRes.data.data || []);
      
      // Fetch UOM from master data
      const uomRes = await axios.get(`${API}/master-data/stores/uom`);
      setUomList(uomRes.data.data || []);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const handleVendorSelect = (vendor) => {
    if (vendor) {
      setFormData(prev => ({
        ...prev,
        vendorName: vendor.vendorName || '',
        vendorCode: vendor.vendorCode || '',
        vendorGst: vendor.gstin || vendor.gstNo || '',
        vendorContact: vendor.phone || '',
        vendorContactPerson: vendor.contactPerson || '',
        vendorEmail: vendor.email || '',
        vendorAddress: vendor.address || '',
        vendorCity: vendor.city || '',
        vendorState: vendor.state || '',
        vendorCountry: vendor.country || '',
      }));
    }
  };

  const handleCommoditySelect = async (index, commodity) => {
    const selectedHsn = commodity.hsnCode || '';
    const selectedUom = commodity.uom || 'Nos';
    const selectedName = commodity.commodityName || '';

    // Check for UOM mismatch with existing consumable stock
    if (selectedHsn) {
      try {
        const res = await axios.get(`${API}/stores/consumable-stock`);
        const stockItems = res.data || [];
        const conflicting = stockItems.filter(
          item => item.hsnCode === selectedHsn && item.unit && item.unit !== selectedUom
        );

        if (conflicting.length > 0) {
          const sameNameConflict = conflicting.find(
            item => (item.itemName || '').toLowerCase() === selectedName.toLowerCase()
          );

          if (sameNameConflict) {
            // Same HSN + same commodity name + different UOM → strong warning
            const proceed = window.confirm(
              `UOM MISMATCH WARNING!\n\n` +
              `"${selectedName}" (HSN: ${selectedHsn}) already exists in Consumable Stock with UOM "${sameNameConflict.unit}".\n` +
              `You are selecting UOM "${selectedUom}".\n\n` +
              `This will cause inconsistency in stock tracking.\n` +
              `Are you sure you want to proceed with "${selectedUom}"?`
            );
            if (!proceed) return;
          } else {
            // Same HSN + different commodity name + different UOM → info alert
            const existingNames = [...new Set(conflicting.map(c => `${c.itemName} (${c.unit})`))].join(', ');
            const proceed = window.confirm(
              `UOM Notice: HSN "${selectedHsn}" is used by other commodities with different UOM:\n` +
              `${existingNames}\n\n` +
              `You are adding "${selectedName}" with UOM "${selectedUom}".\n` +
              `Different commodity names with same HSN can have different UOMs. Continue?`
            );
            if (!proceed) return;
          }
        }
      } catch (err) {
        console.error('UOM validation check failed:', err);
      }
    }

    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = {
        ...newItems[index],
        commodityId: commodity.id || '',
        commodityName: commodity.commodityName || '',
        commodityCode: commodity.commodityCode || '',
        hsnCode: commodity.hsnCode || '',
        gstRate: commodity.gstRate || '',
        // Snapshot the master rate so the override-warning can compare
        // if the user later edits the GST cell. -1 = unknown / no master.
        _masterGstRate: commodity.gstRate ?? -1,
        _gstOverrideAck: false,
        uom: commodity.uom || 'Nos',
        itemType: commodity.itemType || 'material',
      };
      return { ...prev, items: newItems };
    });
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { commodityId: '', commodityName: '', commodityCode: '', hsnCode: '', gstRate: '', quantity: '', uom: poTab === 'service' ? 'Job' : 'Nos', rate: '', amount: 0 }]
    }));
  };

  const handleSACSelect = (index, sac) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = {
        ...newItems[index],
        commodityId: `sac_${sac.sacCode}`,
        commodityName: sac.sacDescription || '',
        commodityCode: sac.sacCode || '',
        hsnCode: sac.sacCode || '',
        gstRate: sac.gstRate || 18,
        _masterGstRate: sac.gstRate ?? -1,
        _gstOverrideAck: false,
        itemType: 'service',
      };
      return { ...prev, items: newItems };
    });
  };

  const removeLineItem = (index) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  // Debounce ref for GST self-healing API calls
  const gstUpdateTimeoutRef = useRef({});

  const updateLineItem = (index, field, value) => {
    // UOM validation when manually changed
    if (field === 'uom') {
      const item = formData.items[index];
      if (item.hsnCode && value !== item.uom) {
        axios.get(`${API}/stores/consumable-stock`).then(res => {
          const stockItems = res.data || [];
          const conflicting = stockItems.filter(
            s => s.hsnCode === item.hsnCode && s.unit && s.unit !== value
          );
          if (conflicting.length > 0) {
            const sameNameConflict = conflicting.find(
              s => (s.itemName || '').toLowerCase() === (item.commodityName || '').toLowerCase()
            );
            if (sameNameConflict) {
              alert(
                `UOM MISMATCH WARNING!\n\n"${item.commodityName}" (HSN: ${item.hsnCode}) exists in Consumable Stock with UOM "${sameNameConflict.unit}". You are changing to "${value}".`
              );
            }
          }
        }).catch(() => {});
      }
    }

    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      
      if (field === 'quantity' || field === 'rate' || field === 'gstRate') {
        const qty = parseFloat(newItems[index].quantity) || 0;
        const rate = parseFloat(newItems[index].rate) || 0;
        const gstRate = parseFloat(newItems[index].gstRate) || 0;
        const baseAmount = qty * rate;
        const gstAmount = baseAmount * (gstRate / 100);
        newItems[index].amount = baseAmount;
        newItems[index].gstAmount = gstAmount;
        newItems[index].totalWithGst = baseAmount + gstAmount;
      }

      // GST override warning: when the user types a gstRate that differs
      // from what's on master for this commodity, warn them once. If they
      // confirm, persist the override on the PO line (already done — the
      // line item already has the new value). We NO LONGER silently push
      // the new rate into the master commodity DB. Master data is updated
      // ONLY through the dedicated Master Data screen — so a one-off
      // vendor-specific override (e.g. SAC 996511 master=5% but Glogistics
      // negotiated 18%) doesn't corrupt the global master for other vendors.
      if (field === 'gstRate' && value !== '' && value !== null) {
        const row = newItems[index];
        const masterRate = parseFloat(row._masterGstRate ?? row.masterGstRate ?? -1);
        const newGstRate = parseFloat(value);
        // Only prompt when:
        //   • There IS a known master rate (we have something to compare to)
        //   • The user-entered rate differs materially
        //   • The user hasn't already acknowledged this override on this row
        if (
          !isNaN(newGstRate) &&
          !isNaN(masterRate) && masterRate >= 0 &&
          Math.abs(newGstRate - masterRate) > 0.01 &&
          !row._gstOverrideAck
        ) {
          // Defer the confirm to the next tick so React state updates first
          // (the cell shows the new value, then asks for confirmation).
          if (gstUpdateTimeoutRef.current[index]) clearTimeout(gstUpdateTimeoutRef.current[index]);
          gstUpdateTimeoutRef.current[index] = setTimeout(() => {
            const ok = window.confirm(
              `You are overriding the master GST rate for "${row.commodityName || 'this item'}".\n\n` +
              `Master rate: ${masterRate}%\n` +
              `Your override: ${newGstRate}%\n\n` +
              `Press OK to use ${newGstRate}% for this PO only. Master data will NOT be changed.\n` +
              `Press Cancel to revert to the master rate (${masterRate}%).`
            );
            setFormData(curr => {
              const items = [...curr.items];
              if (ok) {
                items[index] = { ...items[index], gstRate: newGstRate, _gstOverrideAck: true };
              } else {
                items[index] = { ...items[index], gstRate: masterRate, _gstOverrideAck: false };
              }
              // recompute amount + GST for this row
              const qty = parseFloat(items[index].quantity) || 0;
              const rate = parseFloat(items[index].rate) || 0;
              const r = parseFloat(items[index].gstRate) || 0;
              const base = qty * rate;
              items[index].amount = base;
              items[index].gstAmount = base * (r / 100);
              items[index].totalWithGst = base + items[index].gstAmount;
              return { ...curr, items };
            });
          }, 400);
        }
      }
      
      return { ...prev, items: newItems };
    });
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  const calculateGstTotal = () => {
    return formData.items.reduce((sum, item) => {
      const baseAmount = item.amount || 0;
      const gstRate = parseFloat(item.gstRate) || 0;
      return sum + (baseAmount * gstRate / 100);
    }, 0);
  };

  const calculateMiscTotal = () => {
    return (formData.miscCharges || []).reduce((sum, ch) => sum + (parseFloat(ch.amount) || 0), 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateGstTotal() + calculateMiscTotal();
  };

  const resetForm = () => {
    setFormData({
      vendorName: '',
      vendorCode: '',
      vendorGst: '',
      vendorContact: '',
      vendorContactPerson: '',
      vendorEmail: '',
      vendorAddress: '',
      vendorCity: '',
      vendorState: '',
      vendorCountry: '',
      poDate: new Date().toISOString().split('T')[0],
      deliveryDate: '',
    deliveryEndDate: '',
      paymentTerms: '',
      currency: 'INR',
      termsConditions: [],
      remarks: '',
      items: [{ commodityId: '', commodityName: '', commodityCode: '', hsnCode: '', gstRate: '', quantity: '', uom: poTab === 'service' ? 'Job' : 'Nos', rate: '', amount: 0 }],
      miscCharges: [],
      advancePaymentConfig: { enabled: false, splits: [] },
    });
    setEditingPO(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.vendorName) {
      setMessage({ type: 'error', text: 'Please select a vendor' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    // Validate no backdate for new POs
    if (!editingPO) {
      const today = new Date().toISOString().split('T')[0];
      if (formData.poDate < today) {
        setMessage({ type: 'error', text: 'PO Date cannot be in the past. Backdated POs are not allowed.' });
        setTimeout(() => setMessage(null), 4000);
        return;
      }
    }

    if (formData.items.every(item => !item.commodityName)) {
      setMessage({ type: 'error', text: 'Please add at least one line item' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    try {
      // Normalize advance config (strip if not Advance Payment terms)
      const normalizedAdv = (formData.paymentTerms === 'Advance Payment' && formData.advancePaymentConfig?.enabled)
        ? {
            enabled: true,
            splits: (formData.advancePaymentConfig.splits || []).map(s => ({
              percent: parseFloat(s.percent) || 0,
              trigger: s.trigger,
              daysAfterGRN: s.trigger === 'AfterGRN' ? (parseInt(s.daysAfterGRN) || 0) : null,
            })),
          }
        : { enabled: false, splits: [] };

      const payload = {
        ...formData,
        poType: poTab,
        subtotal: calculateSubtotal(),
        gstTotal: calculateGstTotal(),
        miscChargesTotal: calculateMiscTotal(),
        totalAmount: calculateTotal(),
        status: editingPO ? formData.status : 'Draft',
        advancePaymentConfig: normalizedAdv,
      };

      if (editingPO) {
        await axios.put(`${API}/stores/purchase-orders-v2/${editingPO}`, payload);
        setMessage({ type: 'success', text: 'Purchase Order updated successfully!' });
      } else {
        await axios.post(`${API}/stores/purchase-orders-v2`, payload);
        setMessage({ type: 'success', text: 'Purchase Order created successfully!' });
      }
      
      fetchPurchaseOrders();
      resetForm();
    } catch (error) {
      // Handle Pydantic validation errors which come as array of objects
      let errorMessage = 'Failed to save Purchase Order';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      }
      setMessage({ type: 'error', text: errorMessage });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const handleEdit = (po) => {
    // Switch tab to match the PO type
    if (po.poType === 'service' && poTab !== 'service') setPoTab('service');
    else if (po.poType !== 'service' && poTab !== 'material') setPoTab('material');
    
    setFormData({
      vendorName: po.vendorName || '',
      vendorCode: po.vendorCode || '',
      vendorGst: po.vendorGst || '',
      vendorContact: po.vendorContact || '',
      vendorContactPerson: po.vendorContactPerson || '',
      vendorEmail: po.vendorEmail || '',
      vendorAddress: po.vendorAddress || '',
      vendorCity: po.vendorCity || '',
      vendorState: po.vendorState || '',
      vendorCountry: po.vendorCountry || '',
      poDate: po.poDate || '',
      deliveryDate: po.deliveryDate || '',
      deliveryEndDate: po.deliveryEndDate || '',
      paymentTerms: po.paymentTerms || '',
      currency: po.currency || 'INR', // Load currency from PO
      // Handle both old string format and new array format
      termsConditions: Array.isArray(po.termsConditions) ? po.termsConditions : (po.termsConditions ? [po.termsConditions] : []),
      remarks: po.remarks || '',
      items: po.items?.length > 0 ? po.items : [{ commodityName: '', commodityCode: '', hsnCode: '', gstRate: '', quantity: '', uom: 'Nos', rate: '', amount: 0 }],
      miscCharges: po.miscCharges || [],
      advancePaymentConfig: po.advancePaymentConfig?.enabled
        ? po.advancePaymentConfig
        : { enabled: false, splits: [] },
      status: po.status || 'Draft',
    });
    setEditingPO(po.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this Purchase Order?')) return;
    try {
      await axios.delete(`${API}/stores/purchase-orders-v2/${id}`);
      setMessage({ type: 'success', text: 'Purchase Order deleted!' });
      fetchPurchaseOrders();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete Purchase Order' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const handleDownloadPDF = async (po) => {
    setDownloading(po.id);
    try {
      const response = await axios.get(`${API}/stores/purchase-orders-v2/${po.id}/pdf?t=${Date.now()}`, {
        responseType: 'blob',
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PO_${po.poNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to download PDF' });
      setTimeout(() => setMessage(null), 3000);
    }
    setDownloading(null);
  };

  // Filter POs
  const filteredPOs = purchaseOrders.filter(po => {
    const matchesSearch = !searchTerm || 
      po.poNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      po.vendorName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || po.status === statusFilter;
    const matchesUnemailed = !showOnlyUnemailed || isUnemailed(po);
    return matchesSearch && matchesStatus && matchesUnemailed;
  });

  const unemailedCount = purchaseOrders.filter(isUnemailed).length;

  const getStatusColor = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-700',
      'Sent': 'bg-blue-100 text-blue-700',
      'Confirmed': 'bg-green-100 text-green-700',
      'Partially Received': 'bg-yellow-100 text-yellow-700',
      'Completed': 'bg-emerald-100 text-emerald-700',
      'Cancelled': 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  // Calculate filtered PO value
  const getFilteredPOValue = () => {
    let filtered = purchaseOrders;
    
    if (valueFilterType !== 'all') {
      filtered = purchaseOrders.filter(po => {
        const poDate = new Date(po.poDate || po.createdAt);
        const poYear = poDate.getFullYear();
        const poMonth = poDate.getMonth() + 1;
        
        if (valueFilterType === 'year') {
          return poYear === selectedYear;
        } else if (valueFilterType === 'month') {
          return poMonth === selectedMonth && poYear === new Date().getFullYear();
        } else if (valueFilterType === 'custom') {
          return poYear === selectedYear && poMonth === selectedMonth;
        }
        return true;
      });
    }
    
    // Calculate total value by currency (use stored totalAmount which is the definitive PO value)
    const totals = {};
    filtered.forEach(po => {
      const currency = po.currency || 'INR';
      totals[currency] = (totals[currency] || 0) + (parseFloat(po.totalAmount) || 0);
    });
    
    return { count: filtered.length, totals };
  };

  const poStats = getFilteredPOValue();
  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);
  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];

  return (
    <div className="space-y-6" data-testid="stores-po-page">
      {/* PO Value Stats Card */}
      <div className="bg-gradient-to-r from-amber-600 to-orange-600 rounded-xl p-5 text-white shadow-lg">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <DollarSign size={20} />
              <span className="text-amber-100 text-sm font-medium">Total Purchase Order Value</span>
            </div>
            <div className="flex flex-wrap items-baseline gap-4">
              {Object.keys(poStats.totals).length > 0 ? (
                Object.entries(poStats.totals).map(([currency, value]) => {
                  const curr = CURRENCY_OPTIONS.find(c => c.code === currency) || { symbol: currency };
                  return (
                    <div key={currency} className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold">{curr.symbol}{value.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                      <span className="text-amber-200 text-sm">{currency}</span>
                    </div>
                  );
                })
              ) : (
                <span className="text-3xl font-bold">₹0</span>
              )}
            </div>
            <p className="text-amber-200 text-sm mt-1">{poStats.count} Purchase Order(s)</p>
          </div>
          
          {/* Filter Controls */}
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={valueFilterType}
              onChange={(e) => setValueFilterType(e.target.value)}
              className="px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
            >
              <option value="all" className="text-gray-800">All Time</option>
              <option value="year" className="text-gray-800">This Year</option>
              <option value="month" className="text-gray-800">This Month</option>
              <option value="custom" className="text-gray-800">Custom</option>
            </select>
            
            {(valueFilterType === 'year' || valueFilterType === 'custom') && (
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
              >
                {years.map(year => (
                  <option key={year} value={year} className="text-gray-800">{year}</option>
                ))}
              </select>
            )}
            
            {(valueFilterType === 'month' || valueFilterType === 'custom') && (
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
              >
                {months.map(month => (
                  <option key={month.value} value={month.value} className="text-gray-800">{month.label}</option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg">
            <ShoppingCart className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Stores Purchase Orders</h1>
            <p className="text-sm text-gray-500">Create and manage purchase orders for stores consumables</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchPurchaseOrders}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg hover:opacity-90 transition shadow-md"
            data-testid="create-stores-po-btn"
          >
            <Plus size={18} />
            Create {poTab === 'service' ? 'Service' : ''} PO
          </button>
        </div>
      </div>

      {/* Material PO / Service PO Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex">
          <button
            onClick={() => { setPoTab('material'); setShowForm(false); }}
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold transition border-b-2 ${
              poTab === 'material'
                ? 'border-amber-500 text-amber-700 bg-amber-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="material-po-tab"
          >
            <Package size={18} />
            Material PO
          </button>
          <button
            onClick={() => { setPoTab('service'); setShowForm(false); }}
            className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold transition border-b-2 ${
              poTab === 'service'
                ? 'border-indigo-500 text-indigo-700 bg-indigo-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="service-po-tab"
          >
            <Briefcase size={18} />
            Service PO
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          {message.text}
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className={`p-4 bg-gradient-to-r ${poTab === 'service' ? 'from-indigo-500 to-indigo-600' : 'from-amber-500 to-amber-600'}`}>
            <h2 className="text-lg font-bold text-white">
              {editingPO ? 'Edit' : 'Create New'} {poTab === 'service' ? 'Service' : ''} Purchase Order
            </h2>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Vendor Selection */}
            <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Building2 size={18} className="text-amber-600" />
                Vendor Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Vendor Name <span className="text-red-500">*</span>
                  </label>
                  <VendorDropdown
                    value={formData.vendorCode}
                    displayValue={formData.vendorName ? `${formData.vendorName} (${formData.vendorCode || 'N/A'})` : ''}
                    onSelect={handleVendorSelect}
                    placeholder="Search vendor..."
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Vendor Code</label>
                  <input
                    type="text"
                    value={formData.vendorCode}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">GST No</label>
                  <input
                    type="text"
                    value={formData.vendorGst}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Contact Person</label>
                  <input
                    type="text"
                    value={formData.vendorContactPerson}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Contact Number</label>
                  <input
                    type="text"
                    value={formData.vendorContact}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Vendor Email</label>
                  <input
                    type="email"
                    value={formData.vendorEmail}
                    onChange={(e) => setFormData({ ...formData, vendorEmail: e.target.value })}
                    placeholder="vendor@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-amber-400 outline-none"
                    title="Pre-fills the Email-PO modal. Edit/add if missing."
                    data-testid="stores-po-vendor-email-input"
                  />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
                  <input
                    type="text"
                    value={[formData.vendorAddress, formData.vendorCity, formData.vendorState, formData.vendorCountry].filter(Boolean).join(', ')}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* PO Details */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  PO Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.poDate}
                  min={editingPO ? undefined : new Date().toISOString().split('T')[0]}
                  onChange={(e) => setFormData(prev => ({ ...prev, poDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm"
                  required
                />
                {!editingPO && <p className="text-xs text-gray-400 mt-1">Backdate not allowed</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Delivery Start Date</label>
                <input
                  type="date"
                  value={formData.deliveryDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, deliveryDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm"
                  data-testid="stores-po-delivery-date"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Delivery End Date</label>
                <input
                  type="date"
                  value={formData.deliveryEndDate}
                  min={formData.deliveryDate || undefined}
                  onChange={(e) => setFormData(prev => ({ ...prev, deliveryEndDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm"
                  data-testid="stores-po-delivery-end-date"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Currency <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm"
                  required
                  data-testid="currency-select"
                >
                  {CURRENCY_OPTIONS.map(curr => (
                    <option key={curr.code} value={curr.code}>{curr.code} - {curr.name}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Payment Terms</label>
                <select
                  value={formData.paymentTerms}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      paymentTerms: v,
                      advancePaymentConfig: (v === 'Advance Payment')
                        ? (prev.advancePaymentConfig?.enabled ? prev.advancePaymentConfig : {
                            enabled: true,
                            splits: [{ percent: 100, trigger: 'Advance', daysAfterGRN: null }],
                          })
                        : { enabled: false, splits: [] },
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm"
                  data-testid="payment-terms-select"
                >
                  <option value="">Select...</option>
                  {paymentTermsList.map(pt => (
                    <option key={pt.id} value={pt.paymentName}>{pt.paymentName}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Advance Payment Split Config */}
            {formData.paymentTerms === 'Advance Payment' && (
              <AdvancePaymentSplitConfig
                value={formData.advancePaymentConfig}
                onChange={(next) => setFormData(prev => ({ ...prev, advancePaymentConfig: next }))}
              />
            )}

            {/* Line Items */}
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  {poTab === 'service' ? <Briefcase size={18} className="text-indigo-600" /> : <Package size={18} className="text-amber-600" />}
                  {poTab === 'service' ? 'Service Items' : 'Line Items'}
                </h3>
                <button
                  type="button"
                  onClick={addLineItem}
                  className={`flex items-center gap-1 px-3 py-1.5 text-white text-sm rounded-lg transition ${
                    poTab === 'service' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-amber-600 hover:bg-amber-700'
                  }`}
                >
                  <Plus size={14} />
                  Add {poTab === 'service' ? 'Service' : 'Item'}
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">#</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 w-80" colSpan={2}>
                        {poTab === 'service' ? 'Service (SAC + Name)' : 'Commodity (HSN + Name)'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">GST%</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Qty</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">UOM</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Rate ({formData.currency})</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Amount ({formData.currency})</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {formData.items.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-500">{idx + 1}</td>
                        <td className="px-4 py-3 w-80" colSpan={2}>
                          <HsnCommodityPicker
                            kind={poTab === 'service' ? 'service' : 'commodity'}
                            hsnCode={item.hsnCode}
                            commodityName={item.commodityName}
                            gstRate={item.gstRate}
                            commodityId={item.commodityId}
                            onChange={(patch) => {
                              setFormData(prev => {
                                const newItems = [...prev.items];
                                newItems[idx] = {
                                  ...newItems[idx],
                                  ...patch,
                                  // Service rows mirror code into commodityCode for downstream compatibility
                                  ...(poTab === 'service' && patch.hsnCode !== undefined
                                    ? { commodityCode: patch.hsnCode }
                                    : {}),
                                  itemType: poTab === 'service' ? 'service' : 'material',
                                };
                                // Recompute amount when gstRate updated
                                if (patch.gstRate !== undefined) {
                                  const qty = parseFloat(newItems[idx].quantity) || 0;
                                  const rate = parseFloat(newItems[idx].rate) || 0;
                                  const baseAmount = qty * rate;
                                  newItems[idx].amount = baseAmount;
                                }
                                return { ...prev, items: newItems };
                              });
                            }}
                            testIdPrefix={`po-item-${idx}`}
                          />
                          {item.commodityId && (
                            <CommodityNameSuggestion
                              value={item.commodityName}
                              onPick={(name) => updateLineItem(idx, 'commodityName', name)}
                            />
                          )}
                        </td>
                        {/* HSN column is now embedded in the HsnCommodityPicker above (colSpan=2) */}
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="0"
                            max="28"
                            step="0.1"
                            value={item.gstRate}
                            onChange={(e) => updateLineItem(idx, 'gstRate', e.target.value)}
                            className="w-14 px-2 py-1.5 border border-gray-300 rounded text-sm focus:border-amber-400 outline-none"
                            placeholder="0"
                            data-testid={`gst-rate-${idx}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            step="0.01"
                            value={item.quantity}
                            onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)}
                            placeholder="0"
                            className="w-20 px-2 py-1.5 border border-gray-300 rounded text-sm focus:border-amber-400 outline-none"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={item.uom}
                            onChange={(e) => updateLineItem(idx, 'uom', e.target.value)}
                            className="w-20 px-2 py-1.5 border border-gray-300 rounded text-sm focus:border-amber-400 outline-none"
                          >
                            <option value="">Select</option>
                            {uomList.length > 0 ? (
                              uomList.map(u => (
                                <option key={u.id} value={u.uomCode}>{u.uomCode}</option>
                              ))
                            ) : (
                              <>
                                <option value="Nos">Nos</option>
                                <option value="KGS">KGS</option>
                                <option value="MT">MT</option>
                                <option value="LTR">LTR</option>
                                <option value="PCS">PCS</option>
                              </>
                            )}
                            {poTab === 'service' && (
                              <>
                                <option value="Job">Job</option>
                                <option value="Lump Sum">Lump Sum</option>
                                <option value="Per Trip">Per Trip</option>
                                <option value="Per Day">Per Day</option>
                                <option value="Per Month">Per Month</option>
                                <option value="Per Hour">Per Hour</option>
                                <option value="Sq.ft">Sq.ft</option>
                                <option value="Sq.mt">Sq.mt</option>
                                <option value="Rft">Rft</option>
                              </>
                            )}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            step="0.01"
                            value={item.rate}
                            onChange={(e) => updateLineItem(idx, 'rate', e.target.value)}
                            placeholder="0.00"
                            className="w-24 px-2 py-1.5 border border-gray-300 rounded text-sm focus:border-amber-400 outline-none"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {getCurrencySymbol(formData.currency)}{(item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {formData.items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeLineItem(idx)}
                              className="p-1.5 bg-red-100 text-red-600 rounded hover:bg-red-200 transition"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-amber-50 border-t-2 border-amber-200">
                    <tr>
                      <td colSpan={7} className="px-4 py-2 text-right text-sm text-gray-600">
                        Subtotal:
                      </td>
                      <td colSpan={2} className="px-4 py-2 text-sm font-medium text-gray-800">
                        {getCurrencySymbol(formData.currency)}{calculateSubtotal().toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={7} className="px-4 py-2 text-right text-sm text-gray-600">
                        GST Amount:
                      </td>
                      <td colSpan={2} className="px-4 py-2 text-sm font-medium text-gray-800">
                        {getCurrencySymbol(formData.currency)}{calculateGstTotal().toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                    {calculateMiscTotal() > 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-2 text-right text-sm text-gray-600">
                          Misc. Charges (Non-GST):
                        </td>
                        <td colSpan={2} className="px-4 py-2 text-sm font-medium text-gray-800">
                          {getCurrencySymbol(formData.currency)}{calculateMiscTotal().toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    )}
                    <tr className="border-t border-amber-300">
                      <td colSpan={7} className="px-4 py-3 text-right font-bold text-gray-800">
                        Grand Total ({formData.currency}):
                      </td>
                      <td colSpan={2} className="px-4 py-3 text-lg font-bold text-amber-700">
                        {getCurrencySymbol(formData.currency)}{calculateTotal().toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Miscellaneous Charges (Non-GST) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-gray-700 flex items-center gap-2">
                  Miscellaneous Charges <span className="text-[10px] font-normal text-gray-400">(Non-GST: Auto fare, Loading, Unloading, etc.)</span>
                </label>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    miscCharges: [...(prev.miscCharges || []), { description: '', amount: 0 }]
                  }))}
                  className="text-xs bg-amber-50 text-amber-700 px-3 py-1 rounded-lg hover:bg-amber-100 transition flex items-center gap-1"
                  data-testid="add-misc-charge-btn"
                >
                  <Plus size={12} /> Add Charge
                </button>
              </div>
              {(formData.miscCharges || []).length > 0 && (
                <div className="space-y-2">
                  {formData.miscCharges.map((ch, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-gray-50 p-2 rounded-lg">
                      <span className="text-xs text-gray-400 w-6">{idx + 1}.</span>
                      <input
                        type="text"
                        placeholder="Description (e.g. Auto Fare, Unloading)"
                        value={ch.description}
                        onChange={(e) => {
                          const updated = [...formData.miscCharges];
                          updated[idx] = { ...updated[idx], description: e.target.value };
                          setFormData(prev => ({ ...prev, miscCharges: updated }));
                        }}
                        className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:border-amber-400 focus:ring-1 focus:ring-amber-200 outline-none"
                        data-testid={`misc-charge-desc-${idx}`}
                      />
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-400">{getCurrencySymbol(formData.currency)}</span>
                        <input
                          type="number"
                          placeholder="Amount"
                          value={ch.amount || ''}
                          onChange={(e) => {
                            const updated = [...formData.miscCharges];
                            updated[idx] = { ...updated[idx], amount: parseFloat(e.target.value) || 0 };
                            setFormData(prev => ({ ...prev, miscCharges: updated }));
                          }}
                          className="w-28 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-right focus:border-amber-400 focus:ring-1 focus:ring-amber-200 outline-none"
                          data-testid={`misc-charge-amount-${idx}`}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const updated = formData.miscCharges.filter((_, i) => i !== idx);
                          setFormData(prev => ({ ...prev, miscCharges: updated }));
                        }}
                        className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Terms & Remarks */}
            <div className="space-y-4">
              {/* Multiple Terms & Conditions Selection */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-medium text-gray-600">Terms & Conditions</label>
                  {(() => {
                    const total = formData.termsConditions.length;
                    const valid = formData.termsConditions.filter(id => termsConditionsList.find(t => t.id === id)).length;
                    const orphan = total - valid;
                    return (
                      <span className="text-xs text-gray-400">
                        {valid} selected
                        {orphan > 0 && (
                          <span className="ml-1 text-amber-700 italic" title={`${orphan} term(s) on this PO no longer exist in master data`}>
                            (+{orphan} deleted)
                          </span>
                        )}
                      </span>
                    );
                  })()}
                </div>
                
                {/* Selected Terms Tags */}
                {formData.termsConditions.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2 p-2 bg-gray-50 rounded-lg">
                    {formData.termsConditions.map((termId, index) => {
                      const term = termsConditionsList.find(t => t.id === termId);
                      return term ? (
                        <div key={index} className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 rounded-full text-xs">
                          <span className="max-w-[200px] truncate" title={term.termDescription}>
                            {term.termDescription?.substring(0, 40) || term.termTitle}...
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                termsConditions: prev.termsConditions.filter(id => id !== termId)
                              }));
                            }}
                            className="p-0.5 hover:bg-amber-200 rounded-full"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ) : null;
                    })}
                  </div>
                )}
                
                {/* Add Terms Dropdown */}
                <TermsConditionsDropdown
                  options={termsConditionsList.filter(t => !formData.termsConditions.includes(t.id))}
                  onAdd={(id) => {
                    if (id && !formData.termsConditions.includes(id)) {
                      setFormData(prev => ({
                        ...prev,
                        termsConditions: [...prev.termsConditions, id]
                      }));
                    }
                  }}
                />
              </div>

              {/* Remarks */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Remarks</label>
                <input
                  type="text"
                  value={formData.remarks}
                  onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                  placeholder="Additional remarks..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-sm"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                type="submit"
                className="flex items-center gap-2 px-6 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition font-medium"
              >
                <Save size={18} />
                {editingPO ? 'Update PO' : 'Create PO'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="flex items-center gap-2 px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
              >
                <X size={18} />
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by PO number or vendor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-100 focus:border-amber-400"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-100"
          >
            <option value="all">All Status</option>
            {STATUS_OPTIONS.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          {unemailedCount > 0 && (
            <button
              onClick={() => setShowOnlyUnemailed(v => !v)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition ${
                showOnlyUnemailed
                  ? 'bg-amber-500 text-white border-amber-500 shadow-md hover:bg-amber-600'
                  : 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100'
              }`}
              title={showOnlyUnemailed
                ? `Showing only ${unemailedCount} unemailed PO${unemailedCount === 1 ? '' : 's'} — click to clear filter`
                : `${unemailedCount} PO${unemailedCount === 1 ? '' : 's'} not yet emailed — click to filter`}
              data-testid="stores-unemailed-filter-badge"
            >
              <Mail size={14} />
              <span>{unemailedCount} not emailed{showOnlyUnemailed && ' ·'}</span>
              {showOnlyUnemailed && <X size={14} />}
            </button>
          )}
        </div>
      </div>

      {/* PO List */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <h3 className="font-semibold text-gray-800">Purchase Orders ({filteredPOs.length})</h3>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw size={24} className="animate-spin mx-auto mb-2 text-gray-400" />
            <p className="text-gray-500">Loading purchase orders...</p>
          </div>
        ) : filteredPOs.length === 0 ? (
          <div className="p-8 text-center">
            <ShoppingCart size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">No purchase orders found</p>
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="mt-4 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition"
            >
              Create First PO
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredPOs.map(po => (
              <div key={po.id} className="hover:bg-gray-50 transition">
                {/* PO Header Row */}
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedPO(expandedPO === po.id ? null : po.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <FileText size={24} className="text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                        {po.poNumber}
                        {po.poType === 'service' && (
                          <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-[10px] font-bold rounded-full">SERVICE</span>
                        )}
                      </h4>
                      <p className="text-sm text-gray-500">{po.vendorName}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">₹{(po.totalAmount || 0).toLocaleString('en-IN')}</p>
                      <p className="text-xs text-gray-500">{po.poDate}</p>
                    </div>
                    <EmailStatusBadge row={po} testIdSuffix={po.id} />
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(po.status)}`}>
                      {po.status}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDownloadPDF(po); }}
                        className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition"
                        title="Download PDF"
                        disabled={downloading === po.id}
                      >
                        {downloading === po.id ? <RefreshCw size={16} className="animate-spin" /> : <Download size={16} />}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); openEmailModal(po); }}
                        className="p-2 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 transition"
                        title="Email PO to Vendor"
                        data-testid={`stores-po-email-btn-${po.id}`}
                      >
                        <Mail size={16} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setHistoryPO(po); }}
                        className="p-2 bg-indigo-100 text-indigo-600 rounded-lg hover:bg-indigo-200 transition"
                        title="Edit History"
                        data-testid={`stores-po-history-btn-${po.id}`}
                      >
                        <History size={16} />
                      </button>
                      {canEdit && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEdit(po); }}
                        className="p-2 bg-amber-100 text-amber-600 rounded-lg hover:bg-amber-200 transition"
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(po.id); }}
                          className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                    {expandedPO === po.id ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedPO === po.id && (
                  <div className="px-4 pb-4 bg-gray-50 border-t border-gray-100">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 pt-4">
                      <div>
                        <p className="text-xs text-gray-500">GST No</p>
                        <p className="font-medium text-sm">{po.vendorGst || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Payment Terms</p>
                        <p className="font-medium text-sm">{po.paymentTerms || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Contact</p>
                        <p className="font-medium text-sm">{po.vendorContactPerson || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Expected Delivery</p>
                        <p className="font-medium text-sm">{po.deliveryDate || '-'}</p>
                      </div>
                    </div>
                    
                    {/* Line Items */}
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">
                              {po.poType === 'service' ? 'Service' : 'Commodity'}
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">
                              {po.poType === 'service' ? 'SAC' : 'HSN'}
                            </th>
                            <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600">GST%</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Qty</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">UOM</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Rate</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Amount</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">GST Amt</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {po.items?.map((item, idx) => {
                            const baseAmt = parseFloat(item.amount || 0);
                            const gstPct = parseFloat(item.gstRate || 0);
                            const gstAmt = baseAmt * (gstPct / 100);
                            return (
                              <tr key={idx}>
                                <td className="px-3 py-2">{item.commodityName}</td>
                                <td className="px-3 py-2">{item.hsnCode || '-'}</td>
                                <td className="px-3 py-2 text-center">{gstPct}%</td>
                                <td className="px-3 py-2 text-right">{item.quantity}</td>
                                <td className="px-3 py-2">{item.uom || 'Nos'}</td>
                                <td className="px-3 py-2 text-right">{getCurrencySymbol(po.currency || 'INR')}{parseFloat(item.rate || 0).toLocaleString('en-IN')}</td>
                                <td className="px-3 py-2 text-right font-medium">{getCurrencySymbol(po.currency || 'INR')}{baseAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                <td className="px-3 py-2 text-right text-amber-700">{getCurrencySymbol(po.currency || 'INR')}{gstAmt.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot className="bg-amber-50 border-t border-amber-200">
                          {(() => {
                            const sub = po.items?.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0) || 0;
                            const gst = po.items?.reduce((s, i) => {
                              const a = parseFloat(i.amount) || 0;
                              const g = parseFloat(i.gstRate) || 0;
                              return s + (a * g / 100);
                            }, 0) || 0;
                            return (
                              <>
                                <tr>
                                  <td colSpan={6} className="px-3 py-1 text-right text-xs text-gray-500">Subtotal:</td>
                                  <td colSpan={2} className="px-3 py-1 text-right text-sm">{getCurrencySymbol(po.currency || 'INR')}{sub.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                </tr>
                                <tr>
                                  <td colSpan={6} className="px-3 py-1 text-right text-xs text-gray-500">GST:</td>
                                  <td colSpan={2} className="px-3 py-1 text-right text-sm text-amber-700">{getCurrencySymbol(po.currency || 'INR')}{gst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                </tr>
                                <tr className="border-t border-amber-300">
                                  <td colSpan={6} className="px-3 py-2 text-right font-bold text-gray-800">Grand Total:</td>
                                  <td colSpan={2} className="px-3 py-2 text-right font-bold text-amber-700">{getCurrencySymbol(po.currency || 'INR')}{(sub + gst).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                </tr>
                              </>
                            );
                          })()}
                        </tfoot>
                      </table>
                    </div>
                    
                    {/* Terms & Conditions Display */}
                    {(() => {
                      const ids = po.termsConditions
                        ? (Array.isArray(po.termsConditions) ? po.termsConditions : [po.termsConditions])
                        : [];
                      const resolved = ids
                        .map(id => termsConditionsList.find(t => t.id === id))
                        .filter(Boolean);
                      if (resolved.length === 0) return null;
                      const orphanCount = ids.length - resolved.length;
                      return (
                        <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                          <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center justify-between">
                            <span>Terms &amp; Conditions:</span>
                            {orphanCount > 0 && (
                              <span className="text-[10px] font-normal text-amber-700 italic">
                                {orphanCount} deleted term{orphanCount === 1 ? '' : 's'} hidden
                              </span>
                            )}
                          </p>
                          <ul className="space-y-1">
                            {resolved.map((term, idx) => (
                              <li key={term.id} className="text-sm text-gray-700 flex items-start gap-2">
                                <span className="text-amber-600 font-bold">{idx + 1}.</span>
                                <span>{term.termDescription}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <EditHistoryModal
        open={!!historyPO}
        onClose={() => setHistoryPO(null)}
        title={historyPO ? `${historyPO.poNumber} — Edit History` : 'Edit History'}
        historyUrl={historyPO ? `/stores/purchase-orders-v2/${historyPO.id}/history` : null}
      />
      <EmailModal
        open={!!emailPO}
        onClose={() => setEmailPO(null)}
        title={emailPO?.poType === 'service' ? 'Email Service PO to Vendor' : 'Email Purchase Order'}
        subtitle={emailPO ? `PO ${emailPO.poNumber || emailPO.id} → ${emailPO.vendorName || ''}` : ''}
        emailEndpoint={emailPO ? `/stores/purchase-orders-v2/${emailPO.id}/email` : ''}
        defaultTo={emailPO?.vendorEmail || ''}
        snapshotEmail={emailPO?.snapshotEmail || ''}
        defaultSubject={emailPO ? (
          emailPO.poType === 'service'
            ? `Service Purchase Order ${emailPO.poNumber || emailPO.id}`
            : `Purchase Order ${emailPO.poNumber || emailPO.id}`
        ) : ''}
        defaultBody={emailPO ? [
          `Dear ${emailPO.vendorName || 'Vendor'},`,
          '',
          emailPO.poType === 'service'
            ? `Please find attached our Service Purchase Order ${emailPO.poNumber || emailPO.id}.`
            : `Please find attached our Purchase Order ${emailPO.poNumber || emailPO.id}.`,
          '',
          `Grand Total: ${emailPO.currency || 'INR'} ${(emailPO.totalAmount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`,
          '',
          emailPO.poType === 'service'
            ? 'Kindly acknowledge receipt and confirm execution of services as per the schedule mentioned.'
            : 'Kindly acknowledge receipt and confirm delivery as per the schedule mentioned.',
          '',
          'Regards,',
          'Swift Strips India',
        ].join('\n') : ''}
        onSent={() => fetchPurchaseOrders()}
      />
    </div>
  );
}
